import { Component, ElementRef, EventEmitter, HostListener, Input, NgZone, OnInit, Output, SimpleChanges, ViewChild, OnChanges, OnDestroy } from '@angular/core';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { Subscription } from 'rxjs';
import {
	Booking,
	BookingFilter,
	BookingStatus,
	DisplayedColumn,
	DisplayedColumns,
	OrderByBooking,
	OrderDirection,
	Tag,
	Tenant,
	TenantCurrency,
} from 'src/app/core/graphql/generated/gen-types';

import { UserService } from 'src/app/core/services';
import { ResponsiveService } from 'src/app/core/services/admin/responsive.service';
import { BookingService } from 'src/app/core/services/booking/booking.service';
import { TagService } from 'src/app/core/services/tag/tag.service';
import { SnackBar } from 'src/app/core/utility/snackBar';

import { TenantSettingService } from 'src/app/core/services/tenantSetting/tenant-setting.service';
import {
	UpdateDisplayedColumnsComponent,
	validDisplayedColumns,
} from '../../displayedColumns/update-displayed-columns/update-displayed-columns.component';
import { MatDialog } from '@angular/material/dialog';

dayjs.extend(utc);
dayjs.extend(timezone);

export type BookingTableType =
	| 'upcoming'
	| 'historical'
	| 'payment'
	| 'dashboard'
	| 'flight.onlyMatching'
	| 'flight.all'
	| 'bookings'
	| 'flight.cancelled'
	| 'flight.payment';
@Component({
	selector: 'app-booking-table-is',
	templateUrl: './booking-table-is.component.html',
	styleUrls: ['./booking-table-is.component.scss'],
})
export class BookingTableIsComponent implements OnInit, OnChanges, OnDestroy {
	@Input() type: string;
	@Input() filterValue = '';
	@Input() bookingFilter: BookingFilter;
	// 235 v3: the Open amount chip (only bookings with an open amount, fixed order)
	@Input() openAmountOnly = false;
	@Input() flightId: string;
	@Input() bookings: Booking[] = [];
	@Input() viewState: any;
	@Input() ended = false;
	// Whether the parent flight currently accepts check-in invites (SCH/REA + window
	// open) — mirrors the backend preconditions so the button can't be a guaranteed error
	@Input() checkinInviteAllowed = false;
	@Input() selectedFilterTags: Tag[] = [];
	@Input() selectedFilterRegion: string;
	// 235 flight picker: also list bookings whose validity ends before the flight date
	@Input() includeExpired = false;
	// 235 flight picker: the flight's day key (YYYY-MM-DD, tenant tz) — rows valid only until an
	// earlier day are marked red like expired bookings
	@Input() expiredBeforeKey: string = null;
	// 235 flight picker: tenant timezone in which Booking.validUntil's calendar day is read
	@Input() expiryTimezone = 'Europe/Brussels';
	// 235 final review U2: false when the host persists the view state itself (the booking list
	// recreates this table on every status switch — saving here cost a mutation per switch). The
	// table then only updates the shared viewState object and emits listStateChange when its saved
	// cursor / sort / search changed.
	@Input() persistOnDestroy = true;

	@Output() deleteBooking = new EventEmitter<Booking>();
	@Output() confirmBooking = new EventEmitter<Booking>();
	@Output() inviteBooking = new EventEmitter<Booking>();
	@Output() confirmMailBooking = new EventEmitter<Booking>();
	@Output() editFlight = new EventEmitter<any>();
	@Output() addBookingsToFlight = new EventEmitter<[Booking]>();
	@Output() addAndConfirmBookingsToFlight = new EventEmitter<[Booking]>();
	@Output() removeBookingFromFlight = new EventEmitter<Booking>();
	@Output() confirmBookingToFlight = new EventEmitter<Booking>();
	@Output() showPaymentStatusBooking = new EventEmitter<Booking>();
	// 235: number of loaded rows after each page (lets the list show "found in …" when empty)
	@Output() loaded = new EventEmitter<number>();
	// 235 v3: whether every row of the list is loaded (the filter row shows "N+" otherwise)
	@Output() complete = new EventEmitter<boolean>();
	// 235 flight picker: number of bookings hidden because they expire before the flight date
	@Output() expiredCount = new EventEmitter<number>();
	// 235 final review U2: persistOnDestroy = false → the saved list state changed (host saves it)
	@Output() listStateChange = new EventEmitter<void>();

	@ViewChild(MatSort, { static: true }) sort: MatSort;

	datasourceBookings = null;
	customSortingDataAccessor;

	// Stable row identity for MatTable. The datasource is rebuilt (new MatTableDataSource)
	// on every infinite-scroll load and on input/sort changes; without a trackBy the table
	// destroys and re-creates all 24-column rows each time. Keying by booking id lets Material
	// reuse existing row DOM and render only newly appended bookings — mirrors the flight table.
	trackByBookingId = (_index: number, row: Booking): string => row?.id;

	tenant: string;

	loadTrigger = 80; // percentage of scrollProgress to trigger a new load
	amountOfRequests = 1;
	bookingsLimit = 40;
	bookingSubs: Subscription[] = [];
	isLoadingBookings = true;
	loadingIndex = 0;
	screensize = 3;
	isMobile: boolean;
	isSmall: boolean;
	isXSmall: boolean;
	tableHeight = 0;

	//Ordering & Sorting
	sortDirection: OrderDirection = OrderDirection.Desc;
	sortActive: OrderByBooking = OrderByBooking.BookingDate;
	sortActiveValues = Object.values(OrderByBooking);
	sortEvent = { active: this.sortActive, direction: this.sortDirection };

	cursor: string;
	endAt = false;

	allBookingsLoadedTop = false;
	allBookingsLoadedBottom = false;

	displayedColumnsParent: DisplayedColumn[] = [];
	displayedColumns: string[] = [];
	operatorDisplayedColumns: DisplayedColumns;
	displayedColumnsId = 'bookingsUpcoming';
	validCustom: boolean;

	@ViewChild('scrollContainer', { static: true }) scrollContainer: ElementRef;
	private readonly onScroll = (e: Event) => this.scrollHandler(e);
	scrollProgress: number;

	// Same memoisation as the flight table: getCellStyles is called ~26× per row per
	// change-detection cycle; caching per breakpoint+widths returns a stable object so
	// ngStyle differs skip it (BUG-022 follow-up).
	private cellStylesCache = new Map<string, { [key: string]: string }>();
	bookingCursor = false;
	bookingUpdate = false;
	private expiredCountRequest = 0;
	private refreshSub: Subscription;

	currency: TenantCurrency;
	checkinEnabled = false;

	// 235 v3: the Open amount chip has a fixed order (open amount ascending, newest booking first)
	get isSortDisabled(): boolean {
		return this.openAmountOnly || this.bookingFilter === BookingFilter.OpenAmount;
	}

	private get fixedOpenAmountOrder(): boolean {
		return this.isSortDisabled && !this.flightId;
	}

	/** 235 v3: two-value cursor of the Open amount order (`oa:<openAmount>:<bookingDate>`). */
	private openAmountCursor(booking: Booking): string {
		const amount = booking?.passengerCounters?.openAmount;
		if (typeof amount !== 'number' || !booking?.bookingDate) return null;
		return `oa:${amount}:${dayjs(booking.bookingDate).toISOString()}`;
	}

	constructor(
		private snackBar: SnackBar,
		private bookingService: BookingService,
		private responsiveService: ResponsiveService,
		private tagService: TagService,
		private router: Router,
		private userService: UserService,
		private tenantSettingService: TenantSettingService,
		private dialog: MatDialog,
		private zone: NgZone,
		private translate: TranslateService,
	) {
		this.tenantSettingService.getTenantSetting().valueChanges.subscribe((result) => {
			this.currency = result.data.tenantSetting?.operatorSettings?.currency;
			this.checkinEnabled = result.data.tenantSetting?.operatorSettings?.myflight?.checkinMode === 'ONLINE';
		});
		// responsiveService Subscriptions
		this.responsiveService.SmallChanged.subscribe((isSmall) => {
			this.isSmall = isSmall;
		});
		this.responsiveService.XSmallChanged.subscribe((isXSmall) => {
			this.isXSmall = isXSmall;
		});
		this.responsiveService.screenSizeChanged.subscribe((screensize) => {
			this.screensize = screensize;
		});
		this.responsiveService.viewMobileChanged.subscribe((mobileView) => {
			this.isMobile = mobileView;
			this.displayedColumns = this.getDisplayedColumns();
		});

		// region sorting dataAccessor
		this.customSortingDataAccessor = (item, property) => {
			switch (property) {
				case 'preferredDate':
					if (!item['region'] || !item['region']['name']) {
						return item['preferredDate'];
					} else {
						return item['preferredDate'] + item['region']['name'];
					}
				default:
					return item[property];
			}
		};

		// 235 review: unsubscribed on destroy, so a destroyed table (e.g. after a filter switch)
		// never reloads when the list's refresh icon is clicked
		this.refreshSub = this.bookingService.refreshSearchResults$.subscribe(() => {
			this.resetTable();
			this.getData();
		});

		this.tenantSettingService.getTenantSetting().valueChanges.subscribe({
			next: (result) => {
				this.operatorDisplayedColumns = result.data.tenantSetting.operatorSettings.displayedColumns;
				this.displayedColumns = this.getDisplayedColumns();
				this.tenant = result.data.tenantSetting.name;
			},
		});
	}

	ngOnInit(): void {
		this.adjustTableHeight(window.innerHeight);

		// Scroll events fire every frame; bound in the template they run change detection
		// over every loaded row per event. Listen outside the Angular zone and only
		// re-enter when the infinite-scroll load actually triggers (BUG-022 follow-up).
		this.zone.runOutsideAngular(() => {
			this.scrollContainer.nativeElement.addEventListener('scroll', this.onScroll, { passive: true });
		});

		if (this.type === 'dashboard' || this.type === 'bookings' || this.type.startsWith('flight')) {
			this.getData();
		}
	}

	adjustTableHeight(innerHeight) {
		this.tableHeight = innerHeight - 320;
		if (this.type.startsWith('flight')) {
			this.tableHeight = 600; // fixed height for flight bookings
		}
		if (this.type === 'dashboard' || this.type === 'bookings' || this.type === 'flight.payment' || this.type === 'flight.cancelled') {
			this.tableHeight = null;
		}
	}

	ngOnChanges(changes: SimpleChanges) {
		if (changes.viewState && changes.viewState.currentValue) {
			this.viewState = changes.viewState.currentValue;
			// 235 review U2: a later viewState object (e.g. the host refetched GetUser after a save)
			// only reloads the table when the saved cursor or sort of this list actually changed —
			// otherwise a filter switch would load the list twice
			const firstViewState = !changes.viewState.previousValue;
			const saved = this.savedListState(this.viewState);
			const previous = firstViewState ? null : this.savedListState(changes.viewState.previousValue);
			if (firstViewState || JSON.stringify(saved) !== JSON.stringify(previous)) {
				if (saved) {
					this.cursor = saved.cursor;
					this.sortActive = saved.sortActive;
					this.sortDirection = saved.sortDirection;
				}
				// backwards compatibility check - cursor should be a string
				if (typeof this.cursor !== 'string') {
					this.cursor = null;
				}
				// Do not reset the cursor but all the rest
				this.resetTable(false);
				this.getData();
			}
		}
		// 235 v3: search + tags (+ the other list inputs) often change together (e.g. "Clear all") →
		// reload the list once per change detection, not once per changed input
		const changedAfterFirst = (name: string) =>
			changes[name] && changes[name].currentValue !== changes[name].previousValue && !changes[name].isFirstChange();
		// 235 review U8: the first value is loaded by ngOnInit (flight picker types) — loading it here
		// too fetched the list and the expired count twice on open
		let reload = ['selectedFilterRegion', 'selectedFilterTags', 'type', 'includeExpired', 'bookingFilter', 'openAmountOnly'].some(changedAfterFirst);
		if (changedAfterFirst('filterValue')) {
			this.filterValue = changes.filterValue.currentValue;
			reload = true;
		}
		if (reload) {
			this.resetTable();
			this.getData();
		}

		if (changes.bookings) {
			this.bookings = changes.bookings.currentValue;
			this.datasourceBookings = new MatTableDataSource(this.sortBookings(changes.bookings.currentValue));
			this.datasourceBookings.sortingDataAccessor = this.customSortingDataAccessor;
		}
	}

	/** Cursor + sort this table type restores from the user's view state (upcoming / historical only). */
	private savedListState(viewState: any): { cursor: string; sortActive: OrderByBooking; sortDirection: OrderDirection } | null {
		const list = this.type === 'upcoming' ? viewState?.bookingList?.open : this.type === 'historical' ? viewState?.bookingList?.history : null;
		if (!list) return null;
		return { cursor: list.cursor, sortActive: list.sort?.active, sortDirection: list.sort?.direction };
	}

	ngOnDestroy(): void {
		this.scrollContainer.nativeElement.removeEventListener('scroll', this.onScroll);
		this.refreshSub?.unsubscribe();
		// reset cursor when leaving the page
		// if (!this.bookingCursor) {
		this.cursor = null;
		// }

		// unsubscribe from all subscriptions
		if (this.bookingSubs.length > 0) {
			this.bookingSubs.forEach((sub) => sub.unsubscribe());
		}

		if (this.viewState) {
			const listState = (viewState: any) =>
				JSON.stringify([viewState.bookingList?.open ?? null, viewState.bookingList?.history ?? null, viewState.bookingList?.filter ?? null]);
			const before = listState(this.viewState);
			if (this.type === 'upcoming') {
				this.viewState.bookingList.open = {
					cursor: this.cursor,
					sort: {
						active: this.sortActive,
						direction: this.sortDirection,
					},
				};
			} else if (this.type === 'payment') {
				// 235 review U7 / v3: the Open amount chip has a fixed sort and its own two-value cursor —
				// keep the Active list's saved sort and cursor untouched
			} else if (this.type === 'historical') {
				this.viewState.bookingList.history = {
					cursor: this.cursor,
					sort: {
						active: this.sortActive,
						direction: this.sortDirection,
					},
				};
			}
			this.viewState.bookingList.filter = this.filterValue;
			if (!this.persistOnDestroy) {
				if (listState(this.viewState) !== before) this.listStateChange.emit();
				return;
			}
			this.userService.saveUserViewState(this.viewState).subscribe(
				() => {},
				(err) => this.snackBar.openSnackBarError(err.message),
			);
		}
	}

	@HostListener('window:resize', ['$event'])
	onResize(event): void {
		this.adjustTableHeight(event.target.innerHeight);
		const heightOfTable = event.target.innerHeight - 310;
		const heightOfRows = 49 * this.bookings.length;
		if (heightOfTable >= heightOfRows) {
			this.getData();
		}
	}

	sortBookings(bookings) {
		let sortedBookings = [];
		sortedBookings = sortedBookings.concat(bookings.filter((b) => b.status === BookingStatus.Comp));
		sortedBookings = sortedBookings.concat(bookings.filter((b) => b.status === BookingStatus.Conf));
		sortedBookings = sortedBookings.concat(bookings.filter((b) => b.status === BookingStatus.Invi));
		sortedBookings = sortedBookings.concat(bookings.filter((b) => b.status === BookingStatus.Adde));
		// only the cancelled-flight panel shows detached bookings (OPEN) — keep them visible at the
		// end there. Other tables keep their previous behaviour of hiding other statuses (070, issue #61)
		if (this.type === 'flight.cancelled') {
			sortedBookings = sortedBookings.concat(bookings.filter((b) => !sortedBookings.includes(b)));
		}
		return sortedBookings;
	}

	updateDisplayedColumns() {
		this.dialog.open(UpdateDisplayedColumnsComponent, {
			width: window.innerWidth <= 360 ? '95%' : '80%',
			data: {
				displayedColumnsId: this.displayedColumnsId,
			},
		});
	}

	private getData(): void {
		this.getAmountOfRequests();

		// get booking list depends on type
		if (this.type === 'upcoming') this.displayedColumnsId = 'bookingsOpen';
		if (this.type === 'historical') this.displayedColumnsId = 'bookingsHistory';
		if (this.type === 'payment') this.displayedColumnsId = 'bookingsPayment';
		if (this.type === 'flight.onlyMatching' || this.type === 'bookings') this.displayedColumnsId = 'bookingsFlight';
		if (this.type === 'bookings' && this.ended) this.displayedColumnsId = 'bookingsFlightEnd';
		if (this.type === 'flight.cancelled') this.displayedColumnsId = 'bookingsFlightCancelled';
		if (this.type === 'flight.all') this.displayedColumnsId = 'bookingsFlightAll';
		if (this.type === 'dashboard') this.displayedColumnsId = 'bookingsDashboard';

		if (this.type === 'bookings' || this.type === 'flight.cancelled') {
			this.datasourceBookings = new MatTableDataSource(this.sortBookings(this.bookings));
			this.datasourceBookings.sortingDataAccessor = this.customSortingDataAccessor;
			this.isLoadingBookings = false;
			this.displayedColumns = this.getDisplayedColumns();
			return;
		}
		if (this.type === 'flight.payment') {
			this.displayedColumnsId = 'bookingsFlightPayment';
			this.datasourceBookings = new MatTableDataSource(this.bookings);
			this.datasourceBookings.sortingDataAccessor = this.customSortingDataAccessor;
			this.datasourceBookings.sort = this.sort;
			this.isLoadingBookings = false;
			this.displayedColumns = this.getDisplayedColumns();
			return;
		}
		if (this.type === 'dashboard') {
			this.sortActive = null;
			this.bookingSubs.push(
				this.bookingService.getDashboardBookings().valueChanges.subscribe((result) => {
					this.bookings = result.data.dashboardBookings;
					this.datasourceBookings = new MatTableDataSource(this.bookings);
					this.datasourceBookings.sortingDataAccessor = this.customSortingDataAccessor;
					this.datasourceBookings.sort = this.sort;
					this.isLoadingBookings = false;
				}),
			);
			this.displayedColumns = this.getDisplayedColumns();
			return;
		}
		// in all other cases
		// set class in case type starts with flight
		if (this.type.startsWith('flight')) {
			this.sortDirection = OrderDirection.Desc;
			this.sortActive = OrderByBooking.BookingDate;
		}
		this.getBookingList();
		this.displayedColumns = this.getDisplayedColumns();
	}

	private getBookingList() {
		this.isLoadingBookings = true;
		this.loadingIndex += 1;
		const index = this.loadingIndex;
		const filter: string[] = this.selectedFilterTags.map((tag) => this.tagService.addPrefix(tag)).concat(this.filterValue);
		if (this.selectedFilterRegion) filter.push(this.selectedFilterRegion);
		// in case of flight bookings, we need to add the flight id to the filter
		let flightId = null;
		if (this.type === 'flight.onlyMatching' || this.type === 'flight.all') {
			flightId = this.flightId;
		}

		// in case of onlyMatching, we need to add the flightOnlyMatching flag
		let flightOnlyMatching = null;
		if (this.type === 'flight.onlyMatching') flightOnlyMatching = true;

		// 235: on the first page of the flight picker, count what the expiry rule hides
		if (flightId && !this.cursor) {
			const countRequest = ++this.expiredCountRequest;
			this.bookingSubs.push(
				this.bookingService.bookingPickerExpiredCount(flightId, !!flightOnlyMatching, filter).subscribe({
					next: (result) => {
						if (countRequest === this.expiredCountRequest) this.expiredCount.emit(result.data?.bookingPickerExpiredCount ?? 0);
					},
					error: () => this.expiredCount.emit(0),
				}),
			);
		}

		const bookingSub = this.bookingService
			.getBookingsIS(
				this.type,
				this.bookingsLimit,
				this.cursor,
				filter,
				this.sortActive,
				this.sortDirection,
				this.bookingFilter,
				flightId,
				flightOnlyMatching,
				this.endAt,
				flightId ? this.includeExpired : null,
				flightId ? null : this.openAmountOnly,
			)
			.valueChanges.subscribe(
				(result) => {
					if (index === this.loadingIndex || this.bookingUpdate) {
						this.bookingUpdate = false;
						const page: Booking[] = result.data.getBookingsIS;
						this.bookings = this.bookings.concat(page);
						this.filterOutDuplicates();
						this.datasourceBookings = new MatTableDataSource(this.bookings);
						this.datasourceBookings.sortingDataAccessor = this.customSortingDataAccessor;
						this.datasourceBookings.sort = this.sort;
						if (this.fixedOpenAmountOrder) {
							// 235 v3: the server order is openAmount ASC, bookingDate DESC — continue after its last row
							if (page.length > 0) this.cursor = this.openAmountCursor(page[page.length - 1]);
						} else if (this.bookings.length > 0) {
							this.cursor = this.bookings[this.bookings.length - 1][this.sortActive];
						}
						if (result.data.getBookingsIS.length < this.bookingsLimit) {
							this.allBookingsLoadedTop = this.endAt;
							this.allBookingsLoadedBottom = !this.endAt;
						}
						this.isLoadingBookings = false;
						this.loaded.emit(this.bookings.length);
						this.complete.emit(this.allBookingsLoadedBottom);
						if (this.amountOfRequests > 1) {
							this.amountOfRequests += -1;
							this.getBookingList();
						}
					}
				},
				(err) => {
					this.isLoadingBookings = false;
					this.snackBar.openSnackBarError(err.message);
				},
			);
		this.bookingSubs.push(bookingSub);
	}

	private getAmountOfRequests(): void {
		if (this.bookings.length != 0) {
			this.amountOfRequests = 1;
		} else {
			const heightOfRows = 49 * this.bookingsLimit; //49px is the fixed height of each column in the table
			this.amountOfRequests = Math.ceil(this.tableHeight / heightOfRows);
		}
	}

	private filterOutDuplicates() {
		// keep most recent version (reverse) of a flight in case of duplicates (filter)
		this.bookings = this.bookings.reverse().filter((v, i, a) => a.findIndex((v2) => v2.id === v.id) === i);
		if (this.fixedOpenAmountOrder) {
			// 235 v3: keep the server's fixed order (open amount ascending, newest booking first)
			this.bookings = this.bookings.sort((a, b) => {
				const byAmount = (a.passengerCounters?.openAmount ?? 0) - (b.passengerCounters?.openAmount ?? 0);
				if (byAmount !== 0) return byAmount;
				return dayjs(b.bookingDate).valueOf() - dayjs(a.bookingDate).valueOf();
			});
			return;
		}
		// sort by sortActive and sortDirection (string with localCompare but dates with < and >)
		if (this.sortActive === OrderByBooking.ContactName) {
			this.bookings = this.bookings.sort((a, b) => {
				return (a[this.sortActive] as string).localeCompare(b[this.sortActive] as string);
			});
		} else {
			this.bookings = this.bookings.sort((a, b) => {
				if (!a[this.sortActive]) return -1;
				if (a[this.sortActive] < b[this.sortActive]) {
					return this.sortDirection === 'asc' ? -1 : 1;
				}
				if (a[this.sortActive] > b[this.sortActive]) {
					return this.sortDirection === 'asc' ? 1 : -1;
				}
				return 0;
			});
		}
	}

	isScrollbar() {
		return this.scrollContainer.nativeElement.scrollHeight > this.scrollContainer.nativeElement.clientHeight;
	}

	private resetTable(cursor = true): void {
		// only reset cursor if cursor is true
		if (cursor) {
			this.cursor = null;
		}
		// if there is a scrollbar then scroll to top
		if (this.isScrollbar()) {
			this.scrollContainer.nativeElement.scrollTop = 0;
		}

		this.endAt = false;
		this.bookings = [];
		this.datasourceBookings = new MatTableDataSource(this.bookings);
		this.datasourceBookings.sortingDataAccessor = this.customSortingDataAccessor;
		this.allBookingsLoadedTop = false;
		this.allBookingsLoadedBottom = false;
	}

	getDisplayedColumns() {
		if (!this.operatorDisplayedColumns || !this.displayedColumnsId) return;
		this.validCustom = validDisplayedColumns(
			this.operatorDisplayedColumns[this.displayedColumnsId],
			this.operatorDisplayedColumns[`${this.displayedColumnsId}Default`],
		);
		let displayedColumns = this.validCustom
			? this.operatorDisplayedColumns[this.displayedColumnsId]
			: this.operatorDisplayedColumns[`${this.displayedColumnsId}Default`];

		displayedColumns = displayedColumns.filter((col) => col.active);
		if (this.isMobile) displayedColumns = displayedColumns.filter((col) => col.mobile);

		return displayedColumns.map((col) => col.name);
	}

	bookingClicked(row) {
		this.cursor = row[this.sortActive];
		this.bookingCursor = true;
		this.navigateToBooking(row.id);
	}

	navigateToBooking(id?) {
		let url = '/booking';
		if (id) {
			url = `${url}/${id}`;
		}

		this.router.navigate([url]);
	}

	handleSortEvent(event) {
		if (this.type === 'dashboard') return;
		if (event.direction && this.sortDirection != event.direction) {
			this.sortDirection = event.direction;
		}
		if (event.active && this.sortActive != event.active) {
			this.sortActive = event.active;
		}
		// reset cursor && bookings
		this.resetTable();
		this.getData();
	}

	navigateToFlight(booking?) {
		// set bookingCursor
		let url = '/flight';
		if (booking?.flight?.id) {
			this.cursor = booking[this.sortActive];
			this.bookingCursor = true;
			url = `${url}/${booking.flight.id}`;
		}

		this.router.navigate([url]);
	}

	mergeCandidatesRed(booking: Booking): boolean {
		// in case of mergeCandidates with flights
		if (booking.mergeCandidates && booking.mergeCandidates.length > 0) {
			return booking.mergeCandidates.filter((c) => c.flight?.date).length > 0;
		}
		return false;
	}

	mergeCandidatesYellow(booking: Booking): boolean {
		// in case of mergeCandidates without flights
		if (booking.mergeCandidates && booking.mergeCandidates.length > 0) {
			return booking.mergeCandidates.filter((c) => !c.flight || !c.flight?.id).length > 0 && !this.mergeCandidatesRed(booking);
		}
		return false;
	}

	countAllBookings() {
		return this.bookingsNotOnFlight().length;
	}

	bookingsNotOnFlight() {
		return this.bookings.filter((booking) => booking.flight?.id != this.flightId);
	}

	updateComments(booking, event) {
		if (event) {
			this.bookingUpdate = true;
			const comments = event.save ? event.comments : booking.comments;
			const tags = event.save ? event.tags : booking.tags;
			if (event.save) {
				this.bookingSubs.push(
					this.bookingService.updateBookingComments(booking.id, comments).subscribe(
						() => {},
						(err) => this.snackBar.openSnackBarError(err.message),
					),
				);
				if (tags) {
					// update tags in backend
					this.bookingSubs.push(
						this.bookingService.updateBookingTags(booking.id, tags).subscribe(
							() => {},
							(err) => this.snackBar.openSnackBarError(err.message),
						),
					);
				}
			}
		}
	}

	updatePreferredFlights(booking, event) {
		if (event) {
			this.bookingUpdate = true;
			const preferredFlights = event.save ? event.preferredFlights : booking.preferredFlights;
			if (event.save) {
				// update preferred flights in backend
				this.bookingSubs.push(
					this.bookingService.updatePreferredFlights(booking.id, preferredFlights).subscribe(
						() => {},
						(err) => this.snackBar.openSnackBarError(err.message),
					),
				);
			}
		}
	}

	openUrl(url) {
		if (url) {
			window.open(url);
		}
	}

	getCheckinPendingTooltip(booking): string {
		// checkinNotifiedAt is only fetched by the getFlight query, so only the flight-detail table can show a reliable tooltip
		if (this.type !== 'bookings') return '';
		if (booking.checkinNotifiedAt) {
			return this.translate.instant('CHECKIN.invite_sent_at', { date: dayjs(booking.checkinNotifiedAt).format('D MMM YYYY HH:mm') });
		}
		return this.translate.instant('CHECKIN.invite_not_sent');
	}

	canSendCheckinInvite(booking): boolean {
		return (
			this.type === 'bookings' &&
			this.checkinEnabled &&
			this.checkinInviteAllowed &&
			booking.status === 'CONF' &&
			!booking.onlineCheckinAt
		);
	}

	sendCheckinInvite(booking): void {
		this.bookingSubs.push(
			this.bookingService.sendCheckinNotification(booking.id).subscribe(
				() => this.snackBar.openSnackBar('CHECKIN.invite_sent_success', 'OK', 4000),
				(err) => this.snackBar.openSnackBarError(err.message),
			),
		);
	}

	// Runs outside the Angular zone (see ngOnInit) — only re-enter when a load is
	// actually triggered, so plain scrolling no longer runs change detection.
	scrollHandler(e): void {
		let scrollTop = false;
		// detect scroll top
		if (e.srcElement.scrollTop === 0) {
			scrollTop = true;
			// allow to scroll to top again if not all bookings loaded
			if (!this.allBookingsLoadedTop) {
				e.srcElement.scrollTop = 1;
			}
		}
		const scrollProgress = Math.floor(((e.srcElement.scrollTop + e.srcElement.clientHeight) / e.srcElement.scrollHeight) * 100);
		this.scrollProgress = scrollProgress;
		if (scrollProgress > this.loadTrigger && this.isLoadingBookings === false && !this.allBookingsLoadedBottom) {
			this.zone.run(() => {
				this.endAt = false;
				this.getData();
			});
		}
		// if (scrollTop && !this.allBookingsLoadedTop) {
		// 	if (this.bookings[0]) {
		// 		this.cursor = this.bookings[0][this.sortActive];
		// 	}
		// 	this.endAt = true;
		// 	this.getData();
		// }
	}

	getCellStyles(xsmall, small, desktop) {
		const key = `${this.isXSmall}|${this.isSmall}|${xsmall}|${small}|${desktop}`;
		let styles = this.cellStylesCache.get(key);
		if (!styles) {
			if (this.isXSmall) {
				styles = { 'min-width': `${xsmall}px`, 'max-width': `${small}px`, padding: '0px 8px' };
			} else if (this.isSmall) {
				styles = { 'min-width': `${small}px`, 'max-width': `${desktop}px`, padding: '0px 8px' };
			} else {
				styles = { 'min-width': `${desktop}px`, 'max-width': `${desktop}px`, padding: '0px 8px' };
			}
			this.cellStylesCache.set(key, styles);
		}
		return styles;
	}

	// 235 flight picker: booking valid only until a day before this flight's date
	// Booking.validUntil is the backend's effective validity end (spec 236; null = unlimited); the
	// booking is valid through that calendar day in the tenant timezone (bug-220 rule).
	isExpiredOnFlight(booking: Booking): boolean {
		if (!this.expiredBeforeKey || !booking?.validUntil) return false;
		return this.validUntilDayKey(booking.validUntil) < this.expiredBeforeKey;
	}

	private validUntilDayCache = new Map<string, string>();
	private validUntilDayKey(validUntil: string): string {
		const cacheKey = `${this.expiryTimezone}|${validUntil}`;
		let key = this.validUntilDayCache.get(cacheKey);
		if (key === undefined) {
			key = dayjs(validUntil).tz(this.expiryTimezone).format('YYYY-MM-DD');
			this.validUntilDayCache.set(cacheKey, key);
		}
		return key;
	}

	hasDefaultWeights(booking: Booking): boolean {
		return booking?.passengers?.some(p => p.weightIsDefault) ?? false;
	}
}
